import { CSSProperties } from "react";
import { Download } from 'lucide-react';
import { toast } from 'sonner';
import { downloadMedia } from '@/lib/media/downloadMedia';

interface VideoModalProps {
    isOpen: boolean;
    onClose: () => void;
    videoUrl: string;
}

function getFileNameFromUrl(url: string, fallback = 'video.mp4') {
    try {
        const parsedUrl = new URL(url, window.location.href);
        const rawName = parsedUrl.pathname.split('/').pop() || '';
        const name = rawName || fallback;

        if (name.includes('.')) {
            return name;
        }

        return `${name.replace(/[^a-zA-Z0-9_-]/g, '') || 'video'}.mp4`;
    } catch {
        return fallback;
    }
}

export default function VideoModal({ isOpen, onClose, videoUrl }: VideoModalProps) {
    if (!isOpen) return null;

    async function handleDownload() {
        const fileName = getFileNameFromUrl(videoUrl);

        const result = await downloadMedia({
            url: videoUrl,
            fileName,
            mimeType: 'video/mp4',
        });

        if (result.success) {
            const message =
                result.platform === 'native'
                    ? 'הסרטון נשמר בתיקיית ההורדות'
                    : 'הסרטון הורד בהצלחה';
            toast.success(message, { style: { direction: 'rtl' } });
        } else {
            toast.error('ההורדה נכשלה', { style: { direction: 'rtl' } });
        }
    }

    return (
        <div
            className="max-h-screen"
            style={styles.overlay}
            onClick={(e) => {
                if (e.target === e.currentTarget) {
                    onClose();
                }
            }}
        >
            <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
                <div style={styles.toolbar}>
                    <button style={styles.close} onClick={onClose} aria-label="סגור">
                        ✖
                    </button>
                    <button style={styles.download} onClick={handleDownload} aria-label="הורד סרטון">
                        <Download className="w-5 h-5" />
                    </button>
                </div>

                <video
                    src={videoUrl}
                    controls
                    autoPlay
                    style={{ width: "100%", borderRadius: "8px", height: "91%" }}
                    onClick={(e) => e.stopPropagation()}
                />
            </div>
        </div>
    );
}

const styles: { [key: string]: CSSProperties } = {
    overlay: {
        position: "fixed" as const,
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        background: "rgba(0,0,0,0.7)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 999
    },
    modal: {
        width: "70%",
        maxWidth: "800px",
        height: "90%",
        background: "#fff",
        padding: "16px",
        borderRadius: "12px",
        position: "relative" as const,
        display: 'flex',
        flexDirection: 'column',
    },
    toolbar: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '10px',
        marginBottom: '12px',
    },
    close: {
        border: "none",
        background: "transparent",
        fontSize: "18px",
        cursor: "pointer",
        padding: '8px',
    },
    download: {
        border: "none",
        background: "#000",
        color: "#fff",
        borderRadius: "999px",
        padding: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
    }
};